import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import config from '../config/config';
import axios from 'axios';
import './Calendar.css';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('🔍 Fetching events...');
      console.log('🔑 Token exists:', !!token);
      console.log('🌐 API URL:', config.API_URL);

      const response = await axios.get(`${config.API_URL}/api/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('📅 Events response:', response.data);
      console.log('📊 Number of events:', response.data?.length || 0);

      setEvents(response.data || []);
    } catch (error) {
      console.error('❌ Error fetching events:', error);

      // Log more details about the error
      if (error.response) {
        console.error('❌ Response error:', error.response.status, error.response.statusText);
        console.error('❌ Response data:', error.response.data);
      } else if (error.request) {
        console.error('❌ Request error:', error.request);
      } else {
        console.error('❌ Error message:', error.message);
      }

      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Get calendar data for current month
  const getCalendarData = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

    const calendarDays = [];
    const currentDateIter = new Date(startDate);

    while (currentDateIter <= endDate) {
      calendarDays.push(new Date(currentDateIter));
      currentDateIter.setDate(currentDateIter.getDate() + 1);
    }

    return calendarDays;
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    console.log(`🔍 Looking for events on ${date.toDateString()}`);
    console.log(`📅 Total events available:`, events.length);

    const filteredEvents = events.filter(event => {
      if (!event.date) {
        console.log('❌ Event has no date:', event);
        return false;
      }

      try {
        const eventDate = new Date(event.date);

        // Check if the date is valid
        if (isNaN(eventDate.getTime())) {
          console.log('❌ Invalid event date:', event.date, event);
          return false;
        }

        // Compare dates by year, month, and day only (ignore time)
        const eventYear = eventDate.getFullYear();
        const eventMonth = eventDate.getMonth();
        const eventDay = eventDate.getDate();

        const targetYear = date.getFullYear();
        const targetMonth = date.getMonth();
        const targetDay = date.getDate();

        const isMatch = eventYear === targetYear &&
          eventMonth === targetMonth &&
          eventDay === targetDay;

        if (isMatch) {
          console.log('✅ Event found for date:', date.toDateString(), event);
        }

        return isMatch;
      } catch (error) {
        console.error('❌ Error processing event date:', error, event);
        return false;
      }
    });

    console.log(`📊 Found ${filteredEvents.length} events for ${date.toDateString()}`);
    return filteredEvents;
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long'
    });
  };

  // Navigation functions
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const calendarDays = getCalendarData();
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  if (loading) {
    return (
      <div className="calendar-container">
        <div className="calendar-loading">
          <div className="spinner"></div>
          <span>Chargement du calendrier...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-component">
      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="calendar-nav">
          <button
            className="nav-btn"
            onClick={() => navigateMonth(-1)}
          >
            ←
          </button>
          <h2 className="calendar-title">
            {formatDate(currentDate)}
          </h2>
          <button
            className="nav-btn"
            onClick={() => navigateMonth(1)}
          >
            →
          </button>
        </div>

        <div className="calendar-actions">
          <button
            className="btn btn-sm btn-secondary"
            onClick={goToToday}
          >
            Aujourd'hui
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {/* Day Headers */}
        <div className="calendar-days-header">
          {/* viewMode is removed, so this block is always active */}
          {dayNames.map((day) => (
            <div key={day} className="day-header">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="calendar-days-grid month">
          {calendarDays.map((date, index) => {
            const dayEvents = getEventsForDate(date);
            const isCurrentDay = isToday(date);
            const isCurrentMonthDay = isCurrentMonth(date);

            return (
              <div
                key={index}
                className={`calendar-day ${isCurrentDay ? 'today' : ''} ${!isCurrentMonthDay ? 'other-month' : ''
                  } ${dayEvents.length > 0 ? 'has-events' : ''}`}
              >
                <div className="day-number">
                  {date.getDate()}
                </div>

                {dayEvents.length > 0 && (
                  <div className="day-events">
                    {dayEvents.slice(0, 3).map((event, eventIndex) => (
                      <div
                        key={eventIndex}
                        className={`event-dot ${getEventStatus(event)}`}
                        title={`${event.name || 'Événement'} - Début: ${formatEventTime(event)}`}
                        onClick={() => navigate(`/events/${event.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="event-time">
                          {formatEventTime(event)}
                        </div>
                        <div className="event-title">
                          {event.name || event.description || 'Événement'}
                        </div>
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="more-events">
                        +{dayEvents.length - 3} plus
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Event Summary */}
      {events.length > 0 && (
        <div className="event-summary">
          <h3>Résumé du mois</h3>
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-number">{events.length}</span>
              <span className="stat-label">Total événements</span>
            </div>
            <div className="stat">
              <span className="stat-number">
                {events.filter(e => e.status === 'confirmed').length}
              </span>
              <span className="stat-label">Confirmés</span>
            </div>
            <div className="stat">
              <span className="stat-number">
                {events.filter(e => e.status === 'pending').length}
              </span>
              <span className="stat-label">En attente</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get event status
const getEventStatus = (event) => {
  if (event.status) {
    return event.status;
  }

  // Determine status based on event date
  const eventDate = new Date(event.date);
  const today = new Date();

  if (eventDate < today) {
    return 'completed';
  } else if (event.cancelled) {
    return 'cancelled';
  } else {
    return 'confirmed';
  }
};

// Helper function to format event time
const formatEventTime = (event) => {
  // First try to use the startTime field if it exists
  if (event.startTime) {
    return event.startTime;
  }

  // Fallback to the time from the date field if startTime is not available
  if (event.date) {
    const eventDate = new Date(event.date);
    return eventDate.toLocaleTimeString('fr-FR', { hour: 'numeric', minute: 'numeric' });
  }

  return 'Aucune heure';
};

export default Calendar;

